import { type CounterpartyOpeningImportPreview, OPEN_ITEM_DIRECTION_LABELS } from "@station/contracts";
import { Alert, Button, Card, MoneyText, StatusBadge, useToast } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Download, Upload } from "lucide-react";
import { type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { downloadBlob } from "../../../features/accounting/shared.js";
import {
  CounterpartyPage,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
  useListCrumb,
  useRefreshCounterparties,
} from "../../../features/counterparties/shared.js";
import { uploadFile } from "../../../features/station-import.js";
import { api, API_URL } from "../../../lib/api.js";
import { authStore } from "../../../lib/auth-store.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

export const Route = createFileRoute("/_app/cari/acilis-aktarimi")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: OpeningImportPage,
});

function OpeningImportPage() {
  const { t } = useTranslation();
  const listCrumb = useListCrumb();
  return (
    <CounterpartyPage
      title={t("counterparties.opening.title")}
      description={t("counterparties.opening.description")}
      breadcrumb={[listCrumb, { label: t("counterparties.opening.title") }]}
    >
      {({ companyId }) => <OpeningImport companyId={companyId} />}
    </CounterpartyPage>
  );
}

async function downloadTemplate(): Promise<void> {
  const response = await fetch(`${API_URL}/counterparties/opening-balances/template`, {
    headers: { authorization: `Bearer ${authStore.getAccessToken() ?? ""}` },
  });
  if (!response.ok) throw new Error(String(response.status));
  downloadBlob(await response.blob(), "cari-acilis-sablonu.csv");
}

/** Cari açılış aktarımı: şablon indir → dosya yükle → ÖNİZLEME (satır hataları) → içe aktar. Muhasebe fişi ÜRETMEZ. */
function OpeningImport({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshCounterparties();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileId, setFileId] = useState<string | null>(null);
  const [preview, setPreview] = useState<CounterpartyOpeningImportPreview | null>(null);
  const [busy, setBusy] = useState<"upload" | "commit" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File): Promise<void> {
    setError(null);
    setPreview(null);
    setBusy("upload");
    try {
      const uploaded = await uploadFile(file, "IMPORT");
      setFileId(uploaded);
      setPreview(
        (
          await api.POST("/counterparties/opening-balances/preview", {
            body: { companyId, fileId: uploaded },
          })
        ).data!,
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function commit(): Promise<void> {
    if (!fileId) return;
    setBusy("commit");
    setError(null);
    try {
      const result = (
        await api.POST("/counterparties/opening-balances/import", { body: { companyId, fileId } })
      ).data!;
      await refresh(companyId);
      toast({ tone: "success", title: t("counterparties.opening.imported", { count: result.createdCount }) });
      await navigate({ to: "/cari", search: { state: "balance" } });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="grid gap-4 p-5">
      <Alert tone="info">{t("counterparties.opening.ledgerNote")}</Alert>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() =>
            void downloadTemplate().catch(() => setError(t("counterparties.opening.templateFailed")))
          }
        >
          <Download aria-hidden="true" />
          {t("counterparties.opening.template")}
        </Button>
        <Button onClick={() => inputRef.current?.click()} loading={busy === "upload"}>
          <Upload aria-hidden="true" />
          {t("counterparties.opening.chooseFile")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="sr-only"
          tabIndex={-1}
          aria-label={t("counterparties.opening.chooseFile")}
          onChange={(event) => event.target.files?.[0] && void handleFile(event.target.files[0])}
        />
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {preview ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <StatusBadge tone={preview.valid ? "success" : "danger"}>
              {preview.valid ? t("counterparties.opening.valid") : t("counterparties.opening.invalid")}
            </StatusBadge>
            <span>
              {t("counterparties.opening.totalReceivable")}:{" "}
              <MoneyText value={preview.totalReceivable} className="font-semibold" />
            </span>
            <span>
              {t("counterparties.opening.totalPayable")}:{" "}
              <MoneyText value={preview.totalPayable} className="font-semibold" />
            </span>
          </div>
          {preview.errors.length > 0 ? (
            <Alert tone="danger" title={t("counterparties.opening.fileErrors")}>
              <ul className="grid list-disc gap-0.5 pl-4">
                {preview.errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <SimpleTable
            caption={t("counterparties.opening.previewCaption")}
            className="max-h-96 overflow-y-auto"
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("counterparties.opening.row")}
                </th>
                <th scope="col" className={thClass}>
                  {t("counterparties.fields.counterparty")}
                </th>
                <th scope="col" className={thClass}>
                  {t("counterparties.fields.direction")}
                </th>
                <th scope="col" className={thClass}>
                  {t("counterparties.fields.documentDate")}
                </th>
                <th scope="col" className={thClass}>
                  {t("counterparties.fields.dueDate")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("counterparties.fields.amount")}
                </th>
                <th scope="col" className={thClass}>
                  {t("counterparties.opening.result")}
                </th>
              </tr>
            }
          >
            {preview.rows.map((row) => (
              <tr key={row.rowNo} className="border-t align-top">
                <td className={`${tdClass} tabular`}>{row.rowNo}</td>
                <td className={tdClass}>
                  <span className="font-mono text-xs font-semibold">{row.counterpartyCode}</span>{" "}
                  {row.counterpartyName ?? ""}
                  {row.docNo ? <span className="text-xs text-muted-foreground"> · {row.docNo}</span> : null}
                </td>
                <td className={tdClass}>{row.direction ? OPEN_ITEM_DIRECTION_LABELS[row.direction] : "—"}</td>
                <td className={`${tdClass} tabular`}>{formatDate(row.documentDate)}</td>
                <td className={`${tdClass} tabular`}>{formatDate(row.dueDate)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={row.amount} />
                </td>
                <td className={tdClass}>
                  {row.errors.length === 0 ? (
                    <StatusBadge tone="success">{t("counterparties.opening.ok")}</StatusBadge>
                  ) : (
                    <ul className="grid gap-0.5 text-destructive">
                      {row.errors.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  )}
                </td>
              </tr>
            ))}
          </SimpleTable>
          {!preview.valid ? <Alert tone="warning">{t("counterparties.opening.fixAndRetry")}</Alert> : null}
          <div className="flex justify-end">
            <Button onClick={() => void commit()} disabled={!preview.valid} loading={busy === "commit"}>
              {t("counterparties.opening.commit")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
